import type { TEthereumDescription } from "workers/Evaluation/fns/ethereum";
import {
  getAccount,
  getBalance,
  getBlockNumber,
  getTransactionCount,
  readContract,
  readContracts,
  sendTransaction,
  signMessage,
  waitForTransactionReceipt,
  writeContract,
  type CreateConfigParameters,
  type GetBalanceReturnType,
  type GetTransactionCountReturnType,
  type SignMessageReturnType,
  type WaitForTransactionReceiptReturnType,
} from "@wagmi/core";
import {
  createWalletClient,
  custom,
  erc20Abi,
  formatEther,
  formatUnits,
  parseEther,
  parseUnits,
  UserRejectedRequestError,
  type Chain,
} from "viem";
import { defaultWagmiConfig } from "@web3modal/wagmi/react/config";
import {
  ALL_CHAINS,
  DEFAULT_CHAINS,
  DEFAULT_WAGMI_CONFIG,
  DEFAULT_WEB3_MODAL_CONFIG,
} from "hooks/useWagmi";
import { createWeb3Modal } from "@web3modal/wagmi/react";

export enum EthCallMethodsEnum {
  GetAccountAddress = "getAddress",
  GetNativeBalance = "getNativeBalance",
  GetTokenBalance = "getTokenBalance",
  GetBlockNumber = "getBlockNumber",
  GetNonce = "getNonce",
  SendETH = "sendETH",
  SendERC20Token = "sendERC20Token",
  SignMessage = "signMessage",
  WriteContract = "writeContract",
  ReadContract = "readContract",
}

export function catchWeb3Error(error: any) {
  if (error?.walk) {
    if (!error?.walk((err: unknown) => err instanceof UserRejectedRequestError))
      throw new Error(error?.shortMessage);
  } else {
    throw new Error(error);
  }
}

export default function* ethCallActionSaga(action: TEthereumDescription) {
  const wagmiConfig = (window as any).WAGMI_CONFIG;
  const web3Modal = (window as any).WEB3_MODAL;

  const { payload } = action;
  const { method, data } = payload;
  const { address } = getAccount(wagmiConfig);

  async function getNativeBalance() {
    if (!address) return "0";

    const balance: GetBalanceReturnType = await getBalance(wagmiConfig, {
      address,
    });

    return formatEther(balance.value);
  }

  async function getTokenBalance(tokenAddress: `0x${string}`) {
    if (!tokenAddress) {
      throw new Error("getTokenBalance: tokenAddress is required.");
    }

    if (!address) return "0";

    const [decimals, tokenBalance] = await readContracts(wagmiConfig, {
      contracts: [
        {
          abi: erc20Abi,
          address: tokenAddress,
          functionName: "decimals",
        },
        {
          abi: erc20Abi,
          address: tokenAddress,
          functionName: "balanceOf",
          args: [address!],
        },
      ],
    });

    return formatUnits(
      tokenBalance?.result ?? BigInt(0),
      Number(decimals?.result ?? 1),
    );
  }

  async function sendETH(to?: `0x${string}`, amount?: string) {
    try {
      if (!to || !amount) {
        throw new Error("Send ETH: to and amount are required.");
      }

      if (!address) {
        web3Modal.open();
        return undefined;
      }

      const hash = await sendTransaction(wagmiConfig, {
        to,
        value: parseEther(amount),
      });
      const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
        hash,
      });

      return transactionReceipt;
    } catch (error) {
      catchWeb3Error(error);
    }
  }

  async function sendERC20Token(
    tokenAddress?: `0x${string}`,
    to?: `0x${string}`,
    amount?: string,
  ) {
    try {
      if (!tokenAddress || !to || !amount) {
        throw new Error(
          "Send ERC20 Token: tokenAddress, to and amount are required.",
        );
      }

      if (!address) {
        web3Modal.open();
        return undefined;
      }

      const [symbol, decimals] = await readContracts(wagmiConfig, {
        contracts: [
          {
            abi: erc20Abi,
            address: tokenAddress,
            functionName: "symbol",
          },
          {
            abi: erc20Abi,
            address: tokenAddress,
            functionName: "decimals",
          },
        ],
      });

      const hash = await writeContract(wagmiConfig, {
        abi:
          "USDT" === symbol?.result
            ? [
                {
                  constant: false,
                  inputs: [
                    {
                      name: "_to",
                      type: "address",
                    },
                    {
                      name: "_value",
                      type: "uint256",
                    },
                  ],
                  name: "transfer",
                  outputs: [],
                  payable: false,
                  stateMutability: "nonpayable",
                  type: "function",
                },
              ]
            : erc20Abi,
        address: tokenAddress,
        functionName: "transfer",
        args: [to, parseUnits(amount, decimals?.result ?? 18)],
      });
      const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
        hash,
      });

      return transactionReceipt;
    } catch (error) {
      catchWeb3Error(error);
    }
  }

  async function writeContractHandler(
    contractAddress?: `0x${string}`,
    abi?: any,
    functionName?: string,
    args?: any,
    value?: string,
  ) {
    if (!contractAddress || !abi || !functionName) {
      throw new Error(
        "Write Contract: contractAddress, abi and functionName are required.",
      );
    }

    try {
      if (!address) {
        web3Modal.open();
        return undefined;
      }

      const hash = await writeContract(wagmiConfig, {
        address: contractAddress,
        abi,
        functionName,
        args,
        value: value ? parseEther(value) : undefined,
      });
      const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
        hash,
      });

      return transactionReceipt;
    } catch (error) {
      catchWeb3Error(error);
    }
  }

  async function readContractHandler(
    contractAddress?: `0x${string}`,
    abi?: any,
    functionName?: string,
    args?: any,
  ) {
    if (!contractAddress || !abi || !functionName) {
      throw new Error(
        "Read Contract: contractAddress, abi and functionName are required.",
      );
    }

    try {
      if (!address) {
        web3Modal.open();
        return undefined;
      }

      const result = await readContract(wagmiConfig, {
        address: contractAddress,
        abi,
        functionName,
        args,
      });

      return result;
    } catch (error) {
      catchWeb3Error(error);
    }
  }

  switch (method) {
    case EthCallMethodsEnum.GetAccountAddress:
      return address;

    case EthCallMethodsEnum.GetNativeBalance: {
      const balance: string = yield getNativeBalance();
      return balance;
    }

    case EthCallMethodsEnum.GetTokenBalance: {
      const tokenBalance: string = yield getTokenBalance(data?.tokenAddress);
      return tokenBalance;
    }

    case EthCallMethodsEnum.GetBlockNumber: {
      const blockNumber: bigint = yield getBlockNumber(wagmiConfig);
      return Number(blockNumber);
    }

    case EthCallMethodsEnum.GetNonce: {
      const nonce: GetTransactionCountReturnType = yield getTransactionCount(
        wagmiConfig,
        { address: address! },
      );
      return nonce;
    }

    case EthCallMethodsEnum.SendETH: {
      const transactionReceipt: WaitForTransactionReceiptReturnType =
        yield sendETH(data?.to, data?.amount);
      return transactionReceipt;
    }

    case EthCallMethodsEnum.SendERC20Token: {
      const transactionReceipt: WaitForTransactionReceiptReturnType =
        yield sendERC20Token(data?.tokenAddress, data?.to, data?.amount);

      return transactionReceipt;
    }

    case EthCallMethodsEnum.SignMessage: {
      if (!data?.message) {
        throw new Error("Sign Message: message is required.");
      }

      const signature: SignMessageReturnType = yield signMessage(wagmiConfig, {
        message: data?.message,
      });
      return signature;
    }

    case EthCallMethodsEnum.WriteContract: {
      const transactionReceipt: WaitForTransactionReceiptReturnType =
        yield writeContractHandler(
          data?.contractAddress,
          data?.abi,
          data?.functionName,
          data?.args,
          data?.value,
        );
      return transactionReceipt;
    }

    case EthCallMethodsEnum.ReadContract: {
      const transactionReceipt: WaitForTransactionReceiptReturnType =
        yield readContractHandler(
          data?.contractAddress,
          data?.abi,
          data?.functionName,
          data?.args,
        );
      return transactionReceipt;
    }

    default:
      break;
  }
}
